import axios from "axios";
import { toast } from "react-toastify";
import fetchLibraryProductPrice from "./fetchLibraryProductPrice";

export function getLibraryCartItems() {
    try {
        const parsed = JSON.parse(window.localStorage.getItem("productsData"));
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function setLibraryCartItems(items) {
    window.localStorage.setItem("productsData", JSON.stringify(items));
    window.dispatchEvent(new Event("storage"));
    return items;
}

export function updateLibraryCartItemQuantity(productId, quantity) {
    const nextQuantity = Math.max(1, parseInt(quantity, 10) || 1);
    const next = getLibraryCartItems().map((item) => {
        if (item.id !== productId) return item;
        // Fixed-price items: quantity is locked to the binnendienst-configured default.
        if (item.has_fixed_price) return item;
        return { ...item, quantity: nextQuantity };
    });
    return setLibraryCartItems(next);
}

export function removeLibraryCartItem(productId) {
    const next = getLibraryCartItems().filter((item) => item.id !== productId);
    return setLibraryCartItems(next);
}

/**
 * Refresh library cart item prices from the backend.
 * Fetches all library products and updates cart items with current prices.
 * Removes cart items whose product no longer exists or has no price.
 */
export async function refreshLibraryCartPrices() {
    const items = getLibraryCartItems();
    if (items.length === 0) return items;

    const labelId = window.localStorage.getItem("label_id");
    if (!labelId) return items;

    try {
        const response = await axios.get(`/api/${labelId}/library/products`);
        // Enrich dynamic-priced products via YO so we don't drop them as "no price".
        const freshProducts = await Promise.all(response.data.map((p) => fetchLibraryProductPrice(p)));
        const freshMap = new Map(freshProducts.map((p) => [p.id, p]));

        let changed = false;
        const updated = items
            .filter((item) => {
                const fresh = freshMap.get(item.id);
                if (!fresh || fresh.price === null || fresh.price === undefined) {
                    changed = true;
                    return false;
                }
                return true;
            })
            .map((item) => {
                const fresh = freshMap.get(item.id);
                if (fresh.price !== item.price || fresh.ignore_staffel_discount !== item.ignore_staffel_discount) {
                    changed = true;
                    return { ...item, price: fresh.price, ignore_staffel_discount: fresh.ignore_staffel_discount };
                }
                return item;
            });

        if (changed) {
            return setLibraryCartItems(updated);
        }
        return items;
    } catch {
        return items;
    }
}

/**
 * Add a single library product to the cart (localStorage).
 * Dispatches a storage event so other components (Cart, Table, etc.) react.
 * Shows a toast notification.
 *
 * @param {object} product - The product object from the library API
 * @param {function} [navigateToCheckout] - Optional callback to navigate to checkout
 */
export function addProductToCart(product, navigateToCheckout) {
    // Block on-request products from being added to cart
    if (product.price === null || product.price === undefined) {
        toast.warning("This product is 'on request' and can only be requested via a quotation.", { autoClose: 4000 });
        return;
    }

    const existing = getLibraryCartItems();

    const existingIndex = existing.findIndex((p) => p.id === product.id);
    const hasFixedPrice = product.has_fixed_price === true;

    if (existingIndex >= 0) {
        if (hasFixedPrice) {
            // Quantity is locked to the binnendienst-configured default — re-add is a no-op.
            toast.info("This product is already in your cart at the configured quantity.", { autoClose: 4000 });
            return;
        }
        existing[existingIndex].quantity =
            (existing[existingIndex].quantity || existing[existingIndex].default_quantity || 1) +
            (product.default_quantity || 1);
    } else {
        existing.push({
            id: product.id,
            name: product.name,
            price: product.price,
            discount_multiplier: product.discount_multiplier ?? 1,
            ignore_staffel_discount: product.ignore_staffel_discount ?? false,
            has_fixed_price: hasFixedPrice,
            quantity: product.default_quantity || 1,
            default_quantity: product.default_quantity || 1,
            file: product.file || null,
            preview_image: product.preview_image || null,
            settings: product.settings || null,
            settings_id: product.settings_id || null,
            model_id: product.model_id || null,
            dimensions: product.dimensions || null,
            model_path: product.model_path || null,
            glb_path: product.glb_path || null,
            library_labels: product.labels || [],
        });
    }

    setLibraryCartItems(existing);

    const goToCheckout = navigateToCheckout;

    toast.success(
        <div>
            <span>1 product added to cart</span>
            <button
                className="library-toast-cart-link"
                onClick={() => {
                    if (goToCheckout) goToCheckout();
                }}
            >
                Go to cart &rarr;
            </button>
        </div>,
        { autoClose: 5000 }
    );
}

/**
 * Request a quotation for a single library product.
 * Uses the same generate-quotation endpoint as LibraryCartBar.
 *
 * @param {object} product - The product object from the library API
 * @param {function} [navigateToQuotations] - Optional callback to navigate to quotations page
 */
export async function requestSingleProductQuotation(product, navigateToQuotations) {
    const labelId = window.localStorage.getItem("label_id");
    const baseUrl = window.location.origin + `/api/${labelId}/library`;

    try {
        const response = await axios.post(`${baseUrl}/generate-quotation`, {
            products: [{ product_id: product.id, quantity: product.default_quantity || 1 }],
        });

        const { yo_number } = response.data;

        toast.success(
            <div>
                <span>Quotation {yo_number} created</span>
                {navigateToQuotations && (
                    <button className="library-toast-cart-link" onClick={() => navigateToQuotations()}>
                        View quotation &rarr;
                    </button>
                )}
            </div>,
            { autoClose: 5000 }
        );
    } catch {
        toast.error("Failed to create quotation. Please try again.", { autoClose: 4000 });
    }
}
